import React, { useEffect, useState } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Popover, PopoverAnchor, PopoverContent } from "@/components/ui/popover";
import { Command, CommandList, CommandEmpty, CommandItem } from "@/components/ui/command";
import { useFormContext } from "react-hook-form";
import { MembershipLevel, POINTS_RIGHTS_DUE_DATE, isExplorerMembership, EXPLORER_PURCHASE_PRICES } from "@/types";
import { calculateMembershipLevel } from "@/utils";
import { useAuth } from "@/contexts/AuthContext";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { searchOwners, OwnerSearchResult } from "@/api/owners";
import { Info } from "lucide-react";
import {
  TooltipProvider,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from "@/components/ui/tooltip";

export const MembershipStep = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>((props, ref) => {
  const { setValue, watch, register, formState: { errors } } = useFormContext();
  const contractData = watch();
  const membershipLevel = watch("owner.membershipLevel");
  const isRealEstate = watch("owner.isRealEstate");
  const isExplorer = isExplorerMembership(membershipLevel);
  const [isInitialized, setIsInitialized] = useState(false);
  const [debounceTimer, setDebounceTimer] = useState<NodeJS.Timeout | null>(null);
  const { user } = useAuth();
  const [showText, setShowText] = useState(false);
  const [calculatedLevel, setCalculatedLevel] = useState<string | null>(null);

  // Owner ID autocomplete: search for an existing owner as the user types
  const membershipNumberValue = watch("owner.membershipNumber");
  const debouncedMembershipNumber = useDebouncedValue(membershipNumberValue, 400);
  const [ownerResults, setOwnerResults] = useState<OwnerSearchResult[]>([]);
  const [isOwnerDropdownOpen, setIsOwnerDropdownOpen] = useState(false);

  useEffect(() => {
    const term = typeof debouncedMembershipNumber === "number" && Number.isNaN(debouncedMembershipNumber)
      ? ""
      : debouncedMembershipNumber?.toString().trim();

    if (!term || term.length < 3) {
      setOwnerResults([]);
      setIsOwnerDropdownOpen(false);
      return;
    }

    let cancelled = false;

    searchOwners(term).then((owners) => {
      if (cancelled) return;
      setOwnerResults(owners);
      setIsOwnerDropdownOpen(owners.length > 0);
    });

    return () => {
      cancelled = true;
    };
  }, [debouncedMembershipNumber]);

  const handleSelectOwner = (owner: OwnerSearchResult) => {
    // Only prefill personal/contact details and address - membershipNumber (already typed),
    // membershipLevel and prospectId are specific to this new contract, not carried over.
    setValue("owner.title", owner.title || "", { shouldValidate: true });
    setValue("owner.firstName", owner.firstName || "", { shouldValidate: true });
    setValue("owner.middleName", owner.middleName || "");
    setValue("owner.lastName", owner.lastName || "", { shouldValidate: true });
    setValue("owner.email", owner.email || "", { shouldValidate: true });
    setValue("owner.phone", owner.phone || "");
    setValue("owner.address.country", owner.address?.country || "", { shouldValidate: true });
    setValue("owner.address.postCode", owner.address?.postCode || "");
    setValue("owner.address.stateProvince", owner.address?.stateProvince || "");
    setValue("owner.address.town", owner.address?.town || "");
    setValue("owner.address.buildingNumber", owner.address?.buildingNumber || "");
    setValue("owner.address.street", owner.address?.street || "");
    setIsOwnerDropdownOpen(false);
  };

  // Check if user is a manager
  const isManager = user?.role === 'manager';

  // Check if agreement number should be required (not required for new contracts or for managers)
  // const isAgreementNumberRequired = contractStatus !== ContractStatus.New && !isManager;

  // Auto-calculate membership level when relevant fields change, but only if current value is 'placeholder'
  useEffect(() => {
    // Only run auto-calculation after initialization is complete
    if (!isInitialized) return;

    const currentMembershipLevel = watch('owner.membershipLevel');
    const currentPoints = watch('pointsRightsPurchased');
    const currentIsRealEstate = watch('owner.isRealEstate');

    console.log('Auto-calc check:', {
      currentMembershipLevel,
      currentPoints,
      currentIsRealEstate,
      isPlaceholder: currentMembershipLevel === 'placeholder',
      isInitialized
    });

    // Clear any existing debounce timer
    if (debounceTimer) {
      clearTimeout(debounceTimer);
    }

    // Only auto-calculate if current value is placeholder (unselected state)
    // AND we have actual points entered (ignore default false for isRealEstate)
    // if (currentMembershipLevel === 'placeholder' && currentPoints && currentPoints > 0) {
    //   // Wait 1 second after user stops typing before calculating
    //   const timer = setTimeout(() => {
    //     const calculatedLevel = calculateMembershipLevel(currentPoints, currentIsRealEstate);

    //     console.log('Calculated level (after debounce):', calculatedLevel);

    //     if (calculatedLevel) {
    //       setValue('owner.membershipLevel', calculatedLevel, { shouldValidate: true });
    //     }
    //   }, 1000); // 1 second delay

    //   setDebounceTimer(timer);
    // }

    // Cleanup function to clear timer on unmount
    return () => {
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }
    };
  }, [watch('pointsRightsPurchased'), watch('owner.isRealEstate'), setValue, watch, isInitialized, debounceTimer]);

  // Initialize form values when contract data is loaded from API
  useEffect(() => {
    // Only run initialization once
    if (isInitialized) return;

    console.log('Initialization check:', {
      contractData: contractData?.owner?.membershipLevel,
      currentMembershipLevel: membershipLevel,
      isInitialized
    });

    // Use a timeout to ensure all form values are loaded before initialization
    const initTimeout = setTimeout(() => {
      // Only set owner.membershipLevel if it hasn't been set yet
      if (contractData?.owner?.membershipLevel && !membershipLevel) {
        console.log('Setting membershipLevel from contract data:', contractData.owner.membershipLevel);
        setValue("owner.membershipLevel", contractData.owner.membershipLevel);
        setIsInitialized(true);
      } else if (!membershipLevel && !contractData?.owner?.membershipLevel) {
        // Set initial placeholder state if no value exists
        console.log('Setting initial placeholder state');
        setValue("owner.membershipLevel", "placeholder");
        setIsInitialized(true);
      }

      // Only set pointsRightsDueDate if it hasn't been set yet
      if (!contractData.pointsRightsDueDate && watch("pointsRightsDueDate") !== POINTS_RIGHTS_DUE_DATE) {
        setValue("pointsRightsDueDate", POINTS_RIGHTS_DUE_DATE);
      }

      // Initialize newPointsPurchased if available in contract data
      const currentNewPoints = watch("newPointsPurchased");
      if (contractData?.newPointsPurchased !== undefined && currentNewPoints === undefined) {
        setValue("newPointsPurchased", contractData.newPointsPurchased);
      }

      // Ensure owner.isRealEstate is correctly set and reflected
      if (contractData?.owner?.isRealEstate !== undefined) {
        const isRealEstateValue = Boolean(contractData.owner.isRealEstate);
        if (watch("owner.isRealEstate") !== isRealEstateValue) {
          setValue("owner.isRealEstate", isRealEstateValue);
        }
      } else if (isRealEstate === undefined) {
        setValue("owner.isRealEstate", false); // Default to false if not set
      }

      // Initialize prospectId if available in contract data
      if (contractData?.owner?.prospectId !== undefined && !watch("owner.prospectId")) {
        setValue("owner.prospectId", contractData.owner.prospectId);
      }
    }, 100); // Small delay to ensure all values are loaded

    return () => clearTimeout(initTimeout);
  }, [contractData, setValue, watch, isInitialized, setIsInitialized, membershipLevel, isRealEstate]);

  // Auto-set Explorer defaults when membership level changes
  useEffect(() => {
    if (isExplorer) {
      setValue("termYears", 3);
      setValue("purchasePrice", EXPLORER_PURCHASE_PRICES[membershipLevel] || 0);
      setValue("pointsRightsPurchased", 0);
      setValue("newPointsPurchased", 0);
      setValue("owner.isRealEstate", false);
      setValue("pointPrice", 0);
      setValue("pointsRightsDueDate", POINTS_RIGHTS_DUE_DATE);
    } else if (membershipLevel && membershipLevel !== "placeholder") {
      // Clear Explorer-specific fields when switching away from Explorer
      setValue("owner.prospectId", undefined);
      setValue("purchasePrice", undefined);
    }
  }, [membershipLevel, isExplorer, setValue]);

  return (
    <div {...props} ref={ref} className={`space-y-6 ${props.className || ""}`}>
      {/* First row: Membership Level (moved to top so Explorer selection drives visibility) */}
      <div className="grid grid-cols-3 gap-6">
        <div className="space-y-2">
          <div className="flex items-center space-x-2">
            <Label htmlFor="membershipLevel">Membership Level *</Label>
            {(() => {
              const suggested = calculateMembershipLevel(
                watch("pointsRightsPurchased"),
                watch("owner.isRealEstate")
              );
              return suggested
                ? (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span
                          tabIndex={0}
                          role="button"
                          onClick={() => setValue("owner.membershipLevel", suggested)}
                          style={{ display: "inline-flex" }}
                        >
                          <Info className="h-4 w-4 text-blue-600 cursor-pointer" />
                        </span>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Suggested: {suggested}</p>
                        <p className="text-xs text-blue-600 italic">Click the icon to apply the suggested level</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )
                : null;
            })()}
          </div>
          <div className="relative">
            <div className="flex items-center gap-2">
              <Select
                value={membershipLevel || "placeholder"}
                onValueChange={(value) => setValue("owner.membershipLevel", value === "placeholder" ? "placeholder" : value as MembershipLevel)}
              >
                <SelectTrigger className="h-8 text-sm">
                  <SelectValue placeholder="Please select membership level" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="placeholder">Please select membership level</SelectItem>
                  <SelectItem value={MembershipLevel.Explorer_1_Bed}>{MembershipLevel.Explorer_1_Bed}</SelectItem>
                  <SelectItem value={MembershipLevel.Explorer_2_Bed}>{MembershipLevel.Explorer_2_Bed}</SelectItem>
                  <SelectItem value={MembershipLevel.HOC_Topaz_Plus}>{MembershipLevel.HOC_Topaz_Plus}</SelectItem>
                  <SelectItem value={MembershipLevel.HOC_Sapphire_Plus}>{MembershipLevel.HOC_Sapphire_Plus}</SelectItem>
                  <SelectItem value={MembershipLevel.HOC_Emerald_Plus}>{MembershipLevel.HOC_Emerald_Plus}</SelectItem>
                  <SelectItem value={MembershipLevel.HOC_Crown}>{MembershipLevel.HOC_Crown}</SelectItem>
                </SelectContent>
              </Select>
              
            </div>
          </div>
          {showText && (
            <div className="pl-0 pt-1 text-xs text-muted-foreground">
              Calculated Level: {calculatedLevel || 'N/A'}
            </div>
          )}
          <input
            {...register("owner.membershipLevel", {
              required: "Membership level is required",
              validate: (value) => value !== "placeholder" || "Please select a valid membership level"
            })}
            type="hidden"
          />
        </div>
      </div>

      {/* Second row: Owner ID, Agreement Number, Is Real Estate Owner / Prospect ID */}
      <div className="grid grid-cols-3 gap-6">
        {!isExplorer && (
          <div className="space-y-2">
            <Label htmlFor="membershipNumber">Owner ID *</Label>
            <Popover open={isOwnerDropdownOpen} onOpenChange={setIsOwnerDropdownOpen}>
              <PopoverAnchor asChild>
                <Input
                  id="membershipNumber"
                  type="number"
                  autoComplete="off"
                  {...register("owner.membershipNumber", {
                    required: !isExplorer,
                    valueAsNumber: true,
                  })}
                  className={`h-8 text-sm ${(errors?.owner as any)?.membershipNumber ? "border-destructive ring-destructive" : ""}`}
                />
              </PopoverAnchor>
              <PopoverContent
                align="start"
                className="w-[320px] p-0"
                onOpenAutoFocus={(e) => e.preventDefault()}
                onCloseAutoFocus={(e) => e.preventDefault()}
              >
                <Command shouldFilter={false}>
                  <CommandList>
                    <CommandEmpty>No matching owner found.</CommandEmpty>
                    {ownerResults.map((owner) => (
                      <CommandItem
                        key={owner._id}
                        value={String(owner._id)}
                        onSelect={() => handleSelectOwner(owner)}
                      >
                        <span className="font-medium">{owner.firstName} {owner.lastName}</span>
                        <span className="ml-2 text-muted-foreground">{owner.email}</span>
                      </CommandItem>
                    ))}
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>
        )}
        {!isManager && !isExplorer && (
          <div className="space-y-2">
            <Label htmlFor="agreementNumber">Agreement Number *</Label>
            <Input
              id="agreementNumber"
              type="number"
              {...register("agreementNumber", {
                required: !isManager && !isExplorer,
                valueAsNumber: true,
              })}
              className={`h-8 text-sm ${errors.agreementNumber ? "border-destructive ring-destructive" : ""}`}
            />
          </div>
        )}
        {!isExplorer && (
          <div className="space-y-3">
            <Label>Is Real Estate Owner? *</Label>
            <RadioGroup
              value={isRealEstate === true ? "true" : "false"}
              onValueChange={(value) => setValue("owner.isRealEstate", value === "true")}
              className="flex flex-row space-x-6"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="true" id="real-estate-yes" />
                <Label htmlFor="real-estate-yes" className="font-normal">Yes</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="false" id="real-estate-no" />
                <Label htmlFor="real-estate-no" className="font-normal">No</Label>
              </div>
            </RadioGroup>
            <input
              {...register("owner.isRealEstate", {
                validate: (value) => isExplorer || (value !== undefined && value !== null)
              })}
              type="hidden"
            />
          </div>
        )}
        {isExplorer && (
          <div className="space-y-2">
            <Label htmlFor="owner.prospectId">Prospect ID *</Label>
            <Input
              id="owner.prospectId"
              type="number"
              {...register("owner.prospectId", { 
                required: true,
              valueAsNumber: true, })}
              className="h-8 text-sm"
              placeholder="Enter prospect ID"
            />
          </div>
        )}
      </div>

      {/* Second row: Points Rights Purchased, New Points Purchased, Term Years */}
      <div className="grid grid-cols-3 gap-6">
        {!isExplorer && (
          <div className="space-y-2">
            <Label htmlFor="pointsRightsPurchased">Points Rights Purchased (Total) *</Label>
            <Input
              id="pointsRightsPurchased"
              type="number"
              {...register("pointsRightsPurchased", {
                required: !isExplorer,
                min: 0,
                valueAsNumber: true
              })}
              className={`h-8 text-sm ${errors.pointsRightsPurchased ? "border-destructive ring-destructive" : ""}`}
              placeholder="Enter points rights purchased"
            />
          </div>
        )}
        {!isExplorer && (
          <div className="space-y-2">
            <Label htmlFor="newPointsPurchased">New Points Purchased</Label>
            <Input
              id="newPointsPurchased"
              type="number"
              value={watch("newPointsPurchased") || ''}
              {...register("newPointsPurchased", {
                min: 0,
                valueAsNumber: true
              })}
              className={`h-8 text-sm ${errors.newPointsPurchased ? "border-destructive ring-destructive" : ""}`}
              placeholder="Enter new points purchased"
            />
          </div>
        )}
        <div className="space-y-2">
          <Label htmlFor="termYears">Term Years *</Label>
          {isExplorer ? (
            <Input
              id="termYears"
              type="number"
              value={3}
              readOnly
              className="h-8 text-sm bg-gray-100 text-gray-500 cursor-not-allowed"
            />
          ) : (
            <Select
              value={(contractData?.termYears || 10).toString()}
              onValueChange={(value) => setValue("termYears", parseInt(value))}
            >
              <SelectTrigger className={`h-8 text-sm ${errors.termYears ? "border-destructive ring-destructive" : ""}`}>
                <SelectValue placeholder="Select term" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10 years</SelectItem>
                <SelectItem value="15">15 years</SelectItem>
                <SelectItem value="20">20 years</SelectItem>
              </SelectContent>
            </Select>
          )}
        </div>
      </div>
    </div>
  );
});